import {Breadcrumb, FetchedItem, FetchedSearchItem, FetchedSite, Item, SelectableSite, TreeItem} from '../types/types';
import {PathPickerType} from '..';
import {getMimeType} from "../constants";
import {getObjectFromArray} from "../utils/arrToObj";

const moment = window.moment;

export const fetchSites = () => {
  return fetch(`${window.appContext}/proxy/alfresco-api/-default-/public/alfresco/versions/1/sites?maxItems=1000`, {
    credentials: 'same-origin',
  })
    .then(response => {
      if (response.status !== 200) throw new Error(response.statusText);
      return response.json();
    })
    .then(data =>
      data.list.entries.reduce((acc: { [siteName: string]: SelectableSite }, next: FetchedSite) => ({
        ...acc,
        [next.entry.id]: {
          label: next.entry.title,
          value: next.entry.id,
          nodeRef: `workspace://SpacesStore/${next.entry.guid}`,
        },
      }), {})
    )
    .catch(reason => {
      throw reason;
    });
};

export const fetchFolderChildren = (
  folderNodeRef: string,
  type: PathPickerType,
  page = 1,
  sort = true,
  sortByValue = 'cm:name',
  pageSize = 50,
) => {
  if (!folderNodeRef) {
    return {
      items: [],
      total: 0,
    };
  }
  const folderId: string = folderNodeRef.split('/').reverse()[0];
  return fetch(
    `${window.appContext}/proxy/alfresco-api/-default-/public/alfresco/versions/1/nodes/${folderId}/children?skipCount=${(page - 1) * pageSize}&maxItems=${pageSize}${type === PathPickerType.FOLDER ? '&where=(isFolder=true)' : ''}&orderBy=isFolder DESC,${sortByValue} ${sort ? 'ASC' : 'DESC'}&include=properties,allowableOperations`,
    {
      credentials: 'same-origin',
    }
  )
    .then(response => {
      if (response.status !== 200) throw new Error(response.statusText);
      return response.json();
    })
    .then(data => {
      const total = data.list.pagination.totalItems;
      const isSiteRoot = false;
      return {
        items: data.list.entries.map((item: any) => ({
          name: item.entry.name,
          nodeRef: `workspace://SpacesStore/${item.entry.id}`,
          type: item.entry.isFolder ? PathPickerType.FOLDER : PathPickerType.FILE,
          properties:
            {
              ...item.entry.properties,
              'cm:created': { iso8601: item.entry.createdAt },
              'cm:modified': { iso8601: item.entry.modifiedAt },
              'cm:modifier': {
                firstName: item.entry.modifiedByUser.displayName,
                lastName: item.entry.modifiedByUser.displayName,
                displayName: item.entry.modifiedByUser.displayName,
                userName: item.entry.modifiedByUser.id,
              },
            } || {},
          indicators: [],
          mimetype: 'Default',
          workingCopy: null,
          version: '1.0',
          isLocked: false,
          disabled: item.entry.isFolder ? false : item.entry.allowableOperations ? !item.entry.allowableOperations.includes('update') : true,
        })),
        total,
        isSiteRoot,
      };
    })
    .catch(reason => {
      throw reason;
    });
};

export const fetchTreeChildren = (folderNodeRef: string) => {
  const modifiedNodeRef: string = folderNodeRef.replace(':/', '');

  return fetch(
    `${window.appContext}/proxy/alfresco/slingshot/doclib/treenode/node/${modifiedNodeRef}?children=true`,
    {
      credentials: 'same-origin',
    }
  )
    .then(response => {
      if (response.status !== 200) throw new Error(response.statusText);
      return response.json();
    })
    .then(data => ({
      [data.parent.nodeRef]: data.items.map((item: TreeItem) => ({
        name: item.name,
        nodeRef: item.nodeRef,
        hasChildren: item.hasChildren,
      })),
    }))
    .catch(reason => {
      throw reason;
    });
};

export const search = (
  query: string,
  rootNodeRef: string,
  type: PathPickerType,
  skipCount: number = 0,
  maxItems: number = 10,
) => {
  const rootNodeParam: string = rootNodeRef ? `&rootNodeId=${rootNodeRef.replace('workspace://SpacesStore/', '')}` : '';
  const nodeTypeParam: string = type === PathPickerType.FOLDER ? '&nodeType=cm:folder&nodeType=ctfd:baseFolder' : '';

  return fetch(
    `${window.appContext}/proxy/alfresco-api/-default-/public/alfresco/versions/1/queries/nodes?term=${query}*&skipCount=${skipCount}&maxItems=${maxItems}&include=path,allowableOperations${rootNodeParam}${nodeTypeParam}`,
    {
      credentials: 'same-origin',
    }
  )
    .then(response => {
      if (response.status !== 200) throw new Error(response.statusText);
      return response.json();
    })
    .then(async data => {
      //Issue #1: Due to unknown reason only on production documents which have comments are returned with isFolder: true,
      //nodeType: 'fm:forum' and ' diskuse' postfix in the name. If searching a file, it duplicates documents with
      //comments. To avoid their incorrect appearance in search results the check nodeType !== 'fm:forum' is applied
      //below.
      //Issue #2: In case, when the user has permissions for the folder, but not for some part of its path, its path
      //property has empty object value ({}), which therefore doesn't have elements property. In this case we need to
      //leave path empty.
      return {
        items: data.list.entries.reduce((acc: Item[], next: FetchedSearchItem) => {
          const { path, name, id, isFolder, nodeType, content, allowableOperations } = next.entry;

          if (nodeType !== 'fm:forum') {
            let newPath: Breadcrumb[] = [];

            if (path?.elements && path.elements.length >= 2 && path.elements[3]?.name === 'documentLibrary') {
              newPath = path.elements.slice(2).map((item: { name: string; id: string; }) => ({
                name: item.name,
                nodeRef: `workspace://SpacesStore/${item.id}`,
              }));
            }

            acc.push({
              name,
              nodeRef: `workspace://SpacesStore/${id}`,
              type: isFolder ? PathPickerType.FOLDER : PathPickerType.FILE,
              path: newPath,
              mimetype: isFolder ? 'folder' : getMimeType(content?.mimeType || ''),
              disabled: isFolder ? !Boolean(allowableOperations) : false,
            });
          }
          return acc;
        }, []),
        total: data?.list?.pagination?.totalItems || 0,
      }
    })
    .catch(reason => {
      throw reason;
    });
};

export const fetchItems = async (nodeRefs: string[], hidden: string[] = []) => {
  const promises = nodeRefs.map(item => {
    const itemId: string = item.replace('workspace://SpacesStore/', '');

    return fetch(
      `${window.appContext}/proxy/alfresco-api/-default-/public/alfresco/versions/1/nodes/${itemId}?include=path`
    ).then(data => data.json());
  });

  const responses = await Promise.all(promises);

  return responses.map(item => ({
    name: item.entry.name,
    nodeRef: `workspace://SpacesStore/${item.entry.id}`,
    type: item.entry.isFolder ? PathPickerType.FOLDER : PathPickerType.FILE,
    path:
      item.entry.path?.elements?.slice(2).map((item: { name: string; id: string; }) => ({
        name: item.name === item.id ? '' : item.name,
        nodeRef: `workspace://SpacesStore/${item.id}`,
      })) || [],
    deletable: !hidden.includes(`workspace://SpacesStore/${item.entry.id}`),
    message: '',
  }));
};

export const fetchBreadcrumbs = (folderNodeRef: string, parentPath: boolean = false) => {
  const itemId: string = folderNodeRef.replace('workspace://SpacesStore/', '');

  return fetch(
    `${window.appContext}/proxy/alfresco-api/-default-/public/alfresco/versions/1/nodes/${itemId}?include=path`,
    {
      credentials: 'same-origin',
    }
  )
    .then(response => {
      if (response.status !== 200) throw new Error(response.statusText);
      return response.json();
    })
    .then(data => {
      const path = data.entry.path?.elements.slice(2).map((item: any) => ({
          name: item.name,
          nodeRef: `workspace://SpacesStore/${item.id}`,
        })) || [];

      return parentPath ? path : [...path, { name: data.entry.name, nodeRef: `workspace://SpacesStore/${data.entry.id}` }];
    })
    .catch(reason => {
      throw reason;
    });
};

export const fetchChildrenByFilter = (
  site: string,
  filter: string,
  type: PathPickerType,
  page = 1,
  sort = true,
  sortByValue = 'cm:name',
  pageSize = 50,
) => {
  return fetch(
    `${
      window.appContext
    }/service/components/documentlibrary/data/doclist/${type === PathPickerType.FOLDER ? 'folders' : 'all'}/site/${site}/documentLibrary/?filter=${filter}&size=${pageSize}&pos=${page}&sortAsc=${sort}&sortField=${sortByValue}&view=browse&${moment().unix()}`,
    {
      credentials: 'same-origin',
    }
  )
    .then(response => {
      if (response.status !== 200) throw new Error(response.statusText);
      return response.json();
    })
    .then(data => {
      const total = type === PathPickerType.FOLDER ? data?.metadata?.itemCounts?.folders || 0 : data.totalRecords;

      return ({
        items: data.items.map((item: FetchedItem) => ({
          name: item.displayName,
          nodeRef: item.nodeRef,
          type: item.node.isContainer ? PathPickerType.FOLDER : PathPickerType.FILE,
          properties: item.node.properties || {},
          indicators: item.indicators,
          mimetype: getMimeType(item.node.mimetype),
          workingCopy: item.workingCopy ? item.workingCopy : null,
          version: item.version,
          isLocked: item.node.isLocked,
        })),
        total,
      });
    })
    .catch(reason => {
      throw reason;
    });
};

export const savePreferences = (preferences: any) => {
  return fetch(`${window.appContext}/proxy/alfresco/api/people/${window.Alfresco.constants.USERNAME}/preferences`, {
    credentials: 'same-origin',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      [window.Alfresco.util.CSRFPolicy.getHeader()]: window.Alfresco.util.CSRFPolicy.getToken(),
    },
    body: JSON.stringify(preferences),
  })
    .then(response => {
      if (response.status !== 200) {
        throw new Error(response.statusText);
      }

      return response.json();
    })
    .catch(reason => {
      throw new Error(reason);
    });
};

export const fetchPreferences = () => {
  return fetch(`${window.appContext}/proxy/alfresco/api/people/${window.Alfresco.constants.USERNAME}/preferences`, {
    credentials: 'same-origin',
  })
    .then(response => {
      if (response.status !== 200) {
        throw new Error(response.statusText);
      }

      return response.json();
    })
    .then(preferences => {
      return {
        favouriteDocuments: getObjectFromArray(preferences.org?.alfresco?.share?.documents?.favourites?.split(',')) || {},
        favouriteFolders: getObjectFromArray(preferences.org?.alfresco?.share?.folders?.favourites?.split(',')) || {},
      };
    })
    .catch(reason => {
      throw new Error(reason);
    });
};